import { Lock, Sparkles, Star, Trophy } from 'lucide-react';
import { Badge, Card, EmptyState, ErrorState, LoadingState, SectionHeader } from '@pixlide/ui';
import { formatRelative } from '@/lib/format';
import { useExploits } from '@/api/queries/dashboard';

export function ExploitsPage() {
  const exploits = useExploits();
  const items = exploits.data?.items ?? [];
  const totalXp = exploits.data?.totalXp ?? 0;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <SectionHeader
        title="Mes exploits"
        description="Débloquez des récompenses et devenez un créateur légendaire."
      />

      <div className="flex flex-wrap gap-3">
        <Card className="flex items-center gap-3 px-5 py-3">
          <span aria-hidden className="flex size-9 items-center justify-center rounded-full bg-warning-100 text-warning-500">
            <Star className="size-4" />
          </span>
          <div>
            <p className="text-xs font-medium text-ink-500">Points XP</p>
            <p className="text-sm font-semibold text-ink-900">{totalXp} XP</p>
          </div>
        </Card>
      </div>

      {exploits.isPending ? <LoadingState /> : null}

      {exploits.isError ? (
        <ErrorState
          message="Impossible de charger vos exploits pour le moment."
          onRetry={() => void exploits.refetch()}
        />
      ) : null}

      {exploits.isSuccess && items.length === 0 ? (
        <EmptyState
          icon={<Trophy className="size-10" strokeWidth={1.25} />}
          title="Aucun exploit pour le moment"
          description="Les jalons débloqués sur votre compte apparaîtront ici."
        />
      ) : null}

      {items.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((exploit) => (
            <Card key={exploit.code} className="flex flex-col gap-3 p-5">
              <span
                aria-hidden
                className={`flex size-10 items-center justify-center rounded-input ${
                  exploit.unlocked ? 'bg-brand-600 text-white' : 'bg-ink-50 text-ink-300'
                }`}
              >
                {exploit.unlocked ? <Sparkles className="size-5" /> : <Lock className="size-5" />}
              </span>

              <div>
                <h3 className="font-semibold text-ink-900">{exploit.name}</h3>
                <p className="text-sm text-ink-500">{exploit.description}</p>
              </div>

              <div className="mt-auto flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <Badge tone={exploit.unlocked ? 'brand' : 'neutral'}>
                    {exploit.unlocked ? 'Débloqué' : 'Verrouillé'}
                  </Badge>
                  {exploit.unlocked && exploit.unlockedAt ? (
                    <span className="text-xs text-ink-300">{formatRelative(exploit.unlockedAt)}</span>
                  ) : null}
                </div>
                <span className="flex items-center gap-1 text-xs font-medium text-brand-700">
                  <Star aria-hidden className="size-3.5" />
                  {exploit.xpReward} XP
                </span>
              </div>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}
